import React, { useCallback, useEffect, useState } from 'react';
import { Crown, Heart, Lock, Star } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { apiService, formatApiError } from '../services/api';
import { resolvePhotoUrl } from '../utils/photoUrl';
import { useToast } from '../hooks/use-toast';

// Who Liked You — the backend decides: free users get only a count (402 on identities), Premium users get the list.
export const WhoLikedYou = ({ onUpgrade, onMatched }) => {
  const { toast } = useToast();
  const [count, setCount] = useState(null);
  const [premium, setPremium] = useState(false);
  const [likers, setLikers] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const c = await apiService.swipes.likesReceivedCount();
      setCount(c.data.data.count); setPremium(c.data.data.premium);
      if (c.data.data.premium) {
        const l = await apiService.swipes.likesReceived();
        setLikers(l.data.data || []);
      }
    } catch { setCount(0); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const likeBack = async (u) => {
    setBusyId(u.user_id);
    try {
      const r = await apiService.swipes.swipe(u.user_id, 'like');
      if (r.data?.data?.is_match) { toast({ title: "It's a Match! 🔥", description: `You and ${u.name} liked each other.` }); onMatched?.(); }
      await load();
    } catch (e) { toast({ title: 'Could not like back', description: formatApiError(e), variant: 'destructive' }); }
    finally { setBusyId(null); }
  };

  if (count === null || count === 0) return null;
  return (
    <Card className="p-5 space-y-3" data-testid="who-liked-you">
      <div className="flex items-center gap-2">
        <Heart className="w-5 h-5 text-rose-500 fill-rose-500" />
        <h2 className="text-base md:text-lg font-semibold text-gray-900">{count} {count === 1 ? 'person likes' : 'people like'} you</h2>
        {premium && <span className="ml-auto text-xs font-semibold rounded-full bg-amber-100 text-amber-800 px-2 py-0.5">PREMIUM</span>}
      </div>
      {!premium ? (
        <div className="flex items-center justify-between gap-3 rounded-lg bg-gradient-to-r from-rose-50 to-orange-50 p-3" data-testid="who-liked-you-locked">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-300 blur-[3px]" aria-hidden="true" />
            <p className="text-sm text-gray-700"><Lock className="inline w-3.5 h-3.5 mr-1 text-gray-500" />See who they are with FlameLink Premium.</p>
          </div>
          <Button size="sm" className="bg-rose-500 hover:bg-rose-600 text-white shrink-0" onClick={onUpgrade} data-testid="who-liked-you-upgrade-btn"><Crown className="w-4 h-4 mr-1" />Upgrade</Button>
        </div>
      ) : (
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4" data-testid="who-liked-you-list">
          {likers.map((u) => (
            <div key={u.user_id} className="rounded-lg border border-gray-200 overflow-hidden" data-testid={`liker-${u.user_id}`}>
              <div className="aspect-square bg-gray-100">
                {u.image ? <img src={resolvePhotoUrl(u.image)} alt={u.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">No photo</div>}
              </div>
              <div className="p-2 space-y-1">
                <p className="text-sm font-medium text-gray-900 truncate">{u.name.split(' ')[0]}{u.age ? `, ${u.age}` : ''} {u.super_like && <Star className="inline w-3.5 h-3.5 text-blue-500 fill-blue-500" />}</p>
                {u.your_response ? <p className="text-xs text-gray-500">{u.your_response === 'pass' ? 'You passed' : 'Matched'}</p>
                  : <Button size="sm" variant="outline" className="w-full" disabled={busyId === u.user_id} onClick={() => likeBack(u)} data-testid={`like-back-${u.user_id}`}>Like back</Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default WhoLikedYou;
